import { Plus, Minus } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/shadcn/alert-dialog';
import { TextDiffView } from '../../../components/ui/TextDiffView';

export const DiffListModal = ({
  open,
  onClose,
  field,
  variant = 'list',
  added = [],
  removed = [],
  currentValue,
  nextValue,
}) => {
  const prettyField = field ? field.replace(/_/g, ' ') : '';

  return (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <AlertDialogContent
        data-testid={`diff-list-modal-${field}`}
        className="max-w-3xl"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-blackColor capitalize">
            Full diff — {prettyField}
          </AlertDialogTitle>
        </AlertDialogHeader>

        <div className="max-h-[70vh] overflow-y-auto pr-1">
          {variant === 'list' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="flex flex-col gap-2">
                <span className="flex items-center gap-1 text-greenColor text-sm font-semibold">
                  <Plus size={14} /> Added ({added.length})
                </span>
                {added.length === 0 ? (
                  <span className="text-darkGrayColor italic">Nothing added</span>
                ) : (
                  <ul
                    data-testid={`diff-list-modal-added-${field}`}
                    className="flex flex-col gap-1"
                  >
                    {added.map((v, i) => (
                      <li
                        key={i}
                        className="px-2 py-1 rounded bg-greenColor/10 text-greenColor border border-greenColor/20 whitespace-pre-wrap break-words"
                      >
                        {String(v)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <span className="flex items-center gap-1 text-redColor text-sm font-semibold">
                  <Minus size={14} /> Removed ({removed.length})
                </span>
                {removed.length === 0 ? (
                  <span className="text-darkGrayColor italic">Nothing removed</span>
                ) : (
                  <ul
                    data-testid={`diff-list-modal-removed-${field}`}
                    className="flex flex-col gap-1"
                  >
                    {removed.map((v, i) => (
                      <li
                        key={i}
                        className="px-2 py-1 rounded bg-redColor/10 text-redColor border border-redColor/20 line-through whitespace-pre-wrap break-words"
                      >
                        {String(v)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {variant === 'text' && (
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div className="flex flex-col gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-redColor font-semibold">
                    Current
                  </span>
                  <div
                    data-testid={`diff-list-modal-current-${field}`}
                    className="p-3 rounded bg-redColor/5 text-blackColor border border-redColor/20 whitespace-pre-wrap break-words"
                  >
                    {currentValue == null || currentValue === ''
                      ? '—'
                      : String(currentValue)}
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <span className="text-[11px] uppercase tracking-wider text-greenColor font-semibold">
                    New
                  </span>
                  <div
                    data-testid={`diff-list-modal-next-${field}`}
                    className="p-3 rounded bg-greenColor/5 text-blackColor border border-greenColor/20 whitespace-pre-wrap break-words"
                  >
                    {nextValue == null || nextValue === ''
                      ? '—'
                      : String(nextValue)}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-[11px] uppercase tracking-wider text-purpleColor font-semibold">
                  Diff — word-level highlighting
                </span>
                <TextDiffView
                  testId={`diff-list-modal-diff-${field}`}
                  oldValue={currentValue}
                  newValue={nextValue}
                  splitView
                  leftTitle="Current"
                  rightTitle="New"
                />
              </div>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogAction
            data-testid={`diff-list-modal-close-${field}`}
            className="bg-purpleColor text-white hover:bg-purpleColor/90"
            onClick={onClose}
          >
            Close
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
